import ModalContext from 'contexts/ModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useLoyaltyUserBenefitView from 'loyalty/hooks/useLoyaltyUserBenefitView'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import { useCallback, useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import LineEditAltIcon from 'LuxKit/Icons/line/LineEditAltIcon'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LoyaltyBenefitItemTile from 'loyalty/components/LoyaltyBenefitItemTile'
import LoyaltyBenefitCompactRow from 'loyalty/components/LoyaltyBenefitCompactRow'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import LoyaltyOrderViewTransferModal from './LoyaltyOrderViewTransferModal'
import getUnifiedTransferView from 'checkout/selectors/view/itemViews/getUnifiedTransferView'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import { TasteOfGoldGoldLabel } from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoStyles'
import TasteOfGoldLearnMoreModal from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldLearnMoreModal'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import styled from 'styled-components'
import { rem } from 'polished'

const GoldHeaderSection = styled.div`
  background: ${props => props.theme.palette.product.loyalty.gold.lightBackground};
  padding: ${rem(20)};
`

interface Props {
  order: App.Order
  layout?: 'tile' | 'row'
}

const TRANSFER_IMAGE_ID = 'd2g3rdusms3uckps2sjc'

function LoyaltyOrderViewBenefitsTransferItem({ order, layout = 'tile' }: Props) {
  const intl = useIntl()
  const transferImage: App.Image = useMemo(() => ({
    id: TRANSFER_IMAGE_ID,
    title: intl.formatMessage({
      id: 'b98upCIl9ZXCyGD4wSVA',
      defaultMessage: 'Airport chauffeur service',
      description: 'Alt text for airport chauffeur transfer service image',
    }),
  }), [intl])
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const transferBenefit = useLoyaltyUserBenefitView('airport_transfer')
  const showModal = useContext(ModalContext)
  const dispatch = useAppDispatch()

  const tasteOfGoldCampaign = useAppSelector(getTasteOfGoldPromoCampaign)
  const isTasteOfGoldActive = !!tasteOfGoldCampaign
  const displayTier = isTasteOfGoldActive ? 'gold' as const : tier

  const transferView = useAppSelector(getUnifiedTransferView).data
  const cartOrderId = useAppSelector(state => state.checkout.cart.existingOrder?.id)
  const hasTransfersInCart = transferView.hasTransfers

  const onAddItems = useCallback((items: Array<App.Checkout.TransferItem>) => {
    dispatch(addToCheckoutCart(items, {
      regionCode: order.regionCode,
      currencyCode: order.currencyCode,
      order,
      mode: 'loyalty-redeem-benefits',
    }))
    dispatch(navigateToCheckout())
  }, [dispatch, order])

  const onRedeem = useCallback(() => {
    showModal(<LoyaltyOrderViewTransferModal
      order={order}
      onAddItems={onAddItems}
    />)
  }, [showModal, order, onAddItems])

  const onEdit = useCallback(() => {
    showModal(<LoyaltyOrderViewTransferModal
      defaultStep="details"
      transferView={transferView}
      order={order}
      onAddItems={onAddItems}
    />)
  }, [showModal, transferView, order, onAddItems])

  const openLearnMore = useCallback(() => {
    showModal(<TasteOfGoldLearnMoreModal />)
  }, [showModal])

  const description = intl.formatMessage({
    id: '6gNFD8U0ZKWH97pLojhB',
    defaultMessage: 'Travel in comfort with a private transfer from your home to your airport.',
    description: 'Default description for the airport transfer loyalty benefit',
  })

  const tile = useMemo(() => <LoyaltyBenefitItemTile
    id="airport-chauffeur"
    heading={intl.formatMessage({
      id: 'SY_VSjvfRBR9lBLBacq9',
      defaultMessage: 'Free airport chauffeur service',
      description: 'Heading for airport chauffeur service benefit tile',
    })}
    labels={<>
      {!!tasteOfGoldCampaign && <LoyaltyTierLabel kind="primary" tier="gold">
        {intl.formatMessage({ id: 'LpmAsHS6POM_i6YywEQB', defaultMessage: '{count}x available', description: 'Label showing number of transfer benefits available' }, { count: tasteOfGoldCampaign.available })}
      </LoyaltyTierLabel>}
      {!tasteOfGoldCampaign && !!transferBenefit && <LoyaltyTierLabel kind="primary" tier={displayTier}>
        {transferBenefit.availability === Infinity
          ? intl.formatMessage({ id: 'E1qsF8BHvsiFdVDFkc8X', defaultMessage: 'Unlimited available', description: 'Label when unlimited transfer benefits are available' })
          : intl.formatMessage({ id: 'LpmAsHS6POM_i6YywEQB', defaultMessage: '{count}x available', description: 'Label showing number of transfer benefits available' }, { count: transferBenefit.availability })}
      </LoyaltyTierLabel>}
      {hasTransfersInCart && <StatusLabel variant="confirmation" startIcon={<LineCheckCircleIcon />}>
        {intl.formatMessage({ id: 'kEoUIocGWB47UzVLnl_0', defaultMessage: 'Added', description: 'Status label when transfer benefit has been redeemed' })}
      </StatusLabel>}
    </>}
    image={transferImage}
    actions={<>
      {hasTransfersInCart && <TextButton
        kind="secondary"
        startIcon={<LineEditAltIcon />}
        variant="default"
        onClick={onEdit}
      >
        {intl.formatMessage({ id: 'susgOPkQlIsVK7O6yys7', defaultMessage: 'Edit', description: 'Button to edit an existing loyalty transfer benefit' })}
      </TextButton>}
      {!hasTransfersInCart && <TextButton kind="secondary" onClick={onRedeem}>
        {intl.formatMessage({ id: 'slRYKSaArcWq-OHHK6oN', defaultMessage: 'Redeem', description: 'Button to redeem an available loyalty transfer benefit' })}
      </TextButton>}
    </>}
  >
    <BodyText variant="medium">
      {description}
    </BodyText>
  </LoyaltyBenefitItemTile>, [intl, transferImage, transferBenefit, tasteOfGoldCampaign, displayTier, hasTransfersInCart, onEdit, onRedeem, description])

  if (layout === 'row') {
    // Only treat cart transfers as "added" when the cart belongs to this order.
    const hasAddedForThisOrder = cartOrderId === order.id && hasTransfersInCart
    const editAction = <TextButton kind="secondary" variant="default" size="small" shape="round" startIcon={<LineEditAltIcon />} onClick={onEdit}>
      {intl.formatMessage({ id: 'susgOPkQlIsVK7O6yys7', defaultMessage: 'Edit', description: 'Button to edit an existing loyalty transfer benefit' })}
    </TextButton>
    const addAction = <TextButton kind="secondary" variant="default" size="small" shape="round" startIcon={<LinePlusIcon />} onClick={onRedeem}>
      <CSSBreakpoint as="span" max="mobile">
        {intl.formatMessage({ id: 'tSyiNkLzkGgaIKhSivRH', defaultMessage: 'Add', description: 'Short add-to-booking button label shown on mobile devices' })}
      </CSSBreakpoint>
      <CSSBreakpoint as="span" min="tablet">
        {intl.formatMessage({ id: 'iZfWZcvSob6VJ3UC00LW', defaultMessage: 'Add to booking', description: 'Action button label to add a loyalty benefit to an upcoming booking' })}
      </CSSBreakpoint>
    </TextButton>
    return <LoyaltyBenefitCompactRow
      benefitType="airport_transfer"
      heading={intl.formatMessage({
        id: 'SY_VSjvfRBR9lBLBacq9',
        defaultMessage: 'Free airport chauffeur service',
        description: 'Heading for airport chauffeur service benefit tile',
      })}
      action={hasAddedForThisOrder ? editAction : addAction}
    />
  }

  if (isTasteOfGoldActive) {
    return <GoldHeaderSection>
      <VerticalSpacer gap={20}>
        <VerticalSpacer gap={4}>
          <CSSBreakpoint max="mobile">
            <VerticalSpacer gap={8}>
              <TasteOfGoldGoldLabel>
                {intl.formatMessage({
                  id: 'EQdK2dgX0zrvLI5lsXO7',
                  defaultMessage: 'Limited time only',
                  description: 'Label indicating a time-limited Taste of Gold promotional offer',
                })}
              </TasteOfGoldGoldLabel>
              <Heading variant="heading4">
                {intl.formatMessage({
                  id: 'gKzXjxexLn_DtLt2rJ_g',
                  defaultMessage: 'A Special Gift For You',
                  description: 'Heading for the Taste of Gold promotional offer on benefits page',
                })}
              </Heading>
            </VerticalSpacer>
          </CSSBreakpoint>
          <CSSBreakpoint min="tablet">
            <Group direction="horizontal" verticalAlign="center" gap={8} wrap="wrap">
              <Heading variant="heading4">
                {intl.formatMessage({
                  id: 'gKzXjxexLn_DtLt2rJ_g',
                  defaultMessage: 'A Special Gift For You',
                  description: 'Heading for the Taste of Gold promotional offer on benefits page',
                })}
              </Heading>
              <TasteOfGoldGoldLabel>
                {intl.formatMessage({
                  id: 'EQdK2dgX0zrvLI5lsXO7',
                  defaultMessage: 'Limited time only',
                  description: 'Label indicating a time-limited Taste of Gold promotional offer',
                })}
              </TasteOfGoldGoldLabel>
            </Group>
          </CSSBreakpoint>
          <BodyText variant="medium">
            <FormattedMessage
              id="K6j46IJusaAiMiY5aDSC"
              defaultMessage="As a {programName} {bronzeTier} member, you’re invited to experience {programName} {goldTier}. <detailsLink>Details</detailsLink>"
              description="Description of the Taste of Gold promotional offer for bronze members followed by a link to learn more"
              values={{
                programName: getLoyaltyProgramDisplayName(),
                bronzeTier: getLoyaltyTierDisplayName('bronze'),
                goldTier: getLoyaltyTierDisplayName('gold'),
                detailsLink: chunks => (
                  <TextLink size="medium" underline onClick={openLearnMore}>
                    {chunks}
                  </TextLink>
                ),
              }}
            />
          </BodyText>
        </VerticalSpacer>
        {tile}
      </VerticalSpacer>
    </GoldHeaderSection>
  }

  return tile
}

export default LoyaltyOrderViewBenefitsTransferItem
